import React from 'react';
import { Link } from 'react-router-dom';
import { contactInfo } from '../mock';
import { Mail, Phone, MapPin, ChevronRight, ArrowLeft } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

export function LegalPage({ title, subtitle, updated, children }) {
  const { lang } = useLang();
  return (
    <div className="bg-nv-cream-soft">
      <div className="wine-dark py-12 md:py-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="inline-flex items-center gap-2 text-nv-gold-light text-sm hover:text-nv-gold-light/80 mb-4">
            <ArrowLeft className="w-4 h-4" /> {lang === 'en' ? 'Back to Home' : 'घर वापस'}
          </Link>
          <h1 className="text-3xl md:text-5xl font-bold text-nv-cream">{title}</h1>
          {subtitle && <p className="text-nv-cream/70 mt-2 text-sm md:text-base">{subtitle}</p>}
          <p className="text-nv-gold-light/70 text-xs mt-3">{lang === 'en' ? 'Last updated:' : 'अंतिम अपडेट:'} {updated || 'July 2026'}</p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-14">
        <div className="bg-white rounded-2xl border border-nv-gold/20 shadow-sm p-6 md:p-10 legal-content">
          {children}
          <div className="mt-10 pt-6 border-t border-nv-gold/20">
            <h3 className="font-bold text-nv-wine mb-3">{lang === 'en' ? 'Contact Us' : 'संपर्क करें'}</h3>
            <ul className="space-y-2 text-sm text-nv-muted">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-nv-orange" /> {contactInfo.address}</li>
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-nv-orange" /> <a href={`tel:${contactInfo.phone}`} className="hover:text-nv-orange">{contactInfo.phone}</a></li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-nv-orange" /> <a href={`mailto:${contactInfo.email}`} className="hover:text-nv-orange break-all">{contactInfo.email}</a></li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AboutUs() {
  const { lang } = useLang();
  return (
    <LegalPage title={lang === 'en' ? 'About Us' : 'हमारे बारे में'} subtitle={lang === 'en' ? 'Navavidha Bhakti — Nine forms of devotion, one divine confluence' : 'नवविधा भक्ति — भक्ति के नौ मार्ग, एक दिव्य संगम'}>
      <p>
        Navavidha Bhakti is a devotion-focused technology platform based in the sacred land
        of <b>Deoghar, Jharkhand</b> — home to Baba Baidyanath Dham, one of the twelve
        Jyotirlingas of Bharat. Our mission is to bring the spiritual richness of Sanatan
        Dharma into every devotee’s life, regardless of where they live.
      </p>
      <p>
        The name <i>“Navavidha Bhakti”</i> comes from the ancient scriptures which describe the
        nine paths of devotion — Shravan (listening), Kirtan (chanting), Smaran (remembrance),
        Padasevana (service), Archana (worship), Vandana (prayer), Dasya (servitude),
        Sakhya (friendship) and Atma-nivedana (surrender). Our platform aspires to embody all
        these nine forms in each service we deliver.
      </p>
      <h2>What We Offer</h2>
      <ul>
        <li><b>Online Puja Booking</b> at temples across India including Baba Baidyanath Dham, Kashi Vishwanath, Mahakaleshwar and more.</li>
        <li><b>Sankalp Ceremony</b> in your name and gotra, performed by certified Vedic Pandit Jis.</li>
        <li><b>Live Video Recording</b> of every puja shared privately with you along with the exact Sankalp timing.</li>
        <li><b>Prasad Home Delivery</b> — blessed prasad sent to your doorstep within 7–10 days.</li>
        <li><b>Puja Samagri Add-ons</b> — Vastra, Bhog, Rudraksha, Diya Aarti and more offerings in your name.</li>
      </ul>
      <h2>Our Commitment</h2>
      <p>
        Every puja we conduct follows the proper <b>vidhi-vidhaan</b> as prescribed by Vedic
        scriptures. Our Pandit Jis are personally verified for their experience, character
        and knowledge of Sanskrit mantras. We treat every Sankalp as sacred and every
        devotee as family.
      </p>
      <h2>Business Information</h2>
      <p><b>Legal / Trading Name:</b> Navavidha Bhakti</p>
      <p><b>Registered Address:</b> {contactInfo.address}</p>
      <p><b>Landmark:</b> {contactInfo.landmark}</p>
      <p><b>Domain:</b> {contactInfo.domain}</p>
    </LegalPage>
  );
}

export function ContactPage() {
  const { lang } = useLang();
  return (
    <LegalPage title={lang === 'en' ? 'Contact Us' : 'संपर्क करें'} subtitle={lang === 'en' ? 'We are here for you — anytime, any question' : 'हम आपके लिए हमेशा उपलब्ध हैं'}>
      <p>
        Reach out to us for any query about pujas, samagri, bookings, refunds, or general
        support. Our team responds within 24 hours on business days.
      </p>
      <h2>Our Details</h2>
      <ul>
        <li><b>Business:</b> Navavidha Bhakti</li>
        <li><b>Address:</b> {contactInfo.address}</li>
        <li><b>Landmark:</b> {contactInfo.landmark}</li>
        <li><b>City / State / Pincode:</b> {contactInfo.city} / {contactInfo.state} / {contactInfo.pincode}</li>
        <li><b>Phone / WhatsApp:</b> <a href={`tel:${contactInfo.phone}`}>{contactInfo.phone}</a></li>
        <li><b>Email:</b> <a href={`mailto:${contactInfo.email}`}>{contactInfo.email}</a></li>
      </ul>
      <h2>Support Hours</h2>
      <p>Monday – Saturday: 9:00 AM to 8:00 PM (IST). Sunday: Limited support.</p>
      <h2>Quick Actions</h2>
      <ul>
        <li><a href={`https://wa.me/${contactInfo.whatsapp}`} target="_blank" rel="noreferrer">Chat with us on WhatsApp</a></li>
        <li><a href={`mailto:${contactInfo.email}`}>Email us for detailed queries</a></li>
        <li><Link to="/upcoming-pujas">Browse Upcoming Pujas</Link></li>
      </ul>
    </LegalPage>
  );
}
